import { useState, useRef, useEffect } from 'react';
import type { CareRequest } from '../hooks/useCareRequests';
import StatusBadge from './StatusBadge';

type Status = CareRequest['status'];
const STATUSES: Status[] = ['접수', '진행중', '완료'];

interface Props {
  current: Status;
  onChange: (s: Status) => Promise<void>;
}

export default function StatusDropdown({ current, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handle = async (s: Status) => {
    if (s === current) { setOpen(false); return; }
    setLoading(true);
    setOpen(false);
    await onChange(s);
    setLoading(false);
  };

  return (
    <div ref={ref} className="relative inline-block">
      <button
        onClick={() => setOpen((v) => !v)}
        disabled={loading}
        className="flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity disabled:opacity-50"
      >
        <StatusBadge status={current} />
        {loading
          ? <i className="ri-loader-4-line text-xs text-gray-400 animate-spin"></i>
          : <i className="ri-arrow-down-s-line text-xs text-gray-400"></i>
        }
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-30 bg-white border border-gray-100 rounded-xl overflow-hidden w-28" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => handle(s)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-xs cursor-pointer transition-colors hover:bg-gray-50 ${s === current ? 'font-semibold' : ''}`}
            >
              <StatusBadge status={s} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
